import React, { useState, useEffect, useRef } from "react";
import type { RedditPost } from "../../context/RedditFeedContext";

/**
 * The card list itself — loading/error/empty states plus each post's
 * card, with its suggested-reply editor, regenerate menu, copy button and
 * "Mark replied" toggle. Extracted out of RedditFeedModal so the same list
 * can render inside the chat "+" menu modal AND the Content > Reddit tab
 * without re-implementing the cards twice. Neither host's chrome (dialog
 * shell, page header, tabs) lives here — this component only renders what's
 * inside the scrollable body.
 */

/** "4h ago" from a unix timestamp. Returns null when the backend couldn't supply one. */
export const formatAge = (createdUtc?: number): string | null => {
  if (!createdUtc) return null;
  const minutes = Math.floor((Date.now() / 1000 - createdUtc) / 60);
  if (minutes < 60) return `${Math.max(minutes, 0)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const formatGeneratedAt = (iso?: string | null): string | null => {
  if (!iso) return null;
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return null;
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  // Rolls over to days: the board is served at any age and refreshes weekly,
  // so hours alone produced things like "359h ago" that nobody can read as
  // "about two weeks".
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
};

/** Drop the scheme/host so the meaningful part of the permalink is readable. */
const prettyUrl = (url: string): string =>
  url.replace(/^https?:\/\/(www\.)?reddit\.com/, "reddit.com").replace(/\/$/, "");

const relevanceStyle = (score?: number): string => {
  if (score === undefined || score === null) return "";
  if (score >= 80) return "bg-green-100 text-green-700";
  if (score >= 65) return "bg-amber-100 text-amber-700";
  return "bg-theme-secondary/10 text-theme-fg-muted";
};

/** Static strings so Tailwind's JIT keeps these classes. */
const CLAMP: Record<number, string> = {
  3: "line-clamp-3",
  5: "line-clamp-5",
};

/**
 * Text that collapses to `lines` and offers a toggle only when it genuinely
 * overflows.
 *
 * Measured rather than guessed from character count: a guess is wrong in both
 * directions — a short question with line breaks overflows three lines, and a
 * long one on a wide card doesn't — which meant offering "read more" on text
 * that was already fully visible.
 */
const ExpandableText: React.FC<{
  text: string;
  lines: number;
  moreLabel: string;
  className?: string;
}> = ({ text, lines, moreLabel, className = "" }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      // Only meaningful while clamped — expanded, the two heights always match.
      if (expanded) return;
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    // Fonts loading or the container resizing changes the answer.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines, expanded]);

  return (
    <div className="space-y-1">
      <p
        ref={ref}
        className={`${className} ${expanded ? "" : CLAMP[lines] || "line-clamp-3"}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          {expanded ? "Show less" : moreLabel}
        </button>
      )}
    </div>
  );
};

/**
 * Reply lengths offered per card, as word targets. "Standard" is the default
 * and the band that reads as a complete, self-contained answer — the length
 * that actually gets quoted rather than scrolled past.
 */
const LENGTH_OPTIONS: { label: string; value: number; hint: string }[] = [
  { label: "Short", value: 20, hint: "~20 words" },
  { label: "Standard", value: 50, hint: "~50 words" },
  { label: "In depth", value: 100, hint: "~100 words" },
];

export interface RedditFeedListProps {
  posts: RedditPost[];
  isLoading: boolean;
  /** A background refresh is running while the board stays on screen — unlike
   *  isLoading, this never blanks the panel. */
  isRefreshing?: boolean;
  error: string | null;
  generatedAt: string | null;
  onRefresh: () => void;
  regeneratingUrl: string | null;
  onRegenerate: (url: string, words: number) => void;
  answeredUrls: Set<string>;
  onToggleAnswered: (url: string) => void;
  /** True when the user has replied to several threads in a short window. */
  pacingWarning?: boolean;
  /**
   * Shown as a button during the blocking loading state — the modal uses it
   * to let the user dismiss the dialog without cancelling the in-flight
   * build. The embedded Content-tab host has nothing to dismiss, so it omits
   * this prop and the button doesn't render.
   */
  onDismissLoading?: () => void;
  /** Read-only cards — used on the Replied tab, where nothing here is
   *  actionable (no regenerate, no copy, no unmark) beyond viewing the
   *  archived reply. Defaults to the normal interactive board. */
  readOnly?: boolean;
  /** Shown in the empty state in place of the default "No posts found." —
   *  the Replied tab and a never-built Open board read differently. */
  emptyMessage?: string;
  /** Shown under the spinner during the blocking loading state, in place of
   *  the default "Finding relevant posts…" — that copy describes building a
   *  new board, which doesn't fit the Replied tab's read of an existing one. */
  loadingMessage?: string;
}

export const RedditFeedList: React.FC<RedditFeedListProps> = ({
  posts,
  isLoading,
  isRefreshing = false,
  error,
  generatedAt,
  onRefresh,
  regeneratingUrl,
  onRegenerate,
  answeredUrls,
  onToggleAnswered,
  pacingWarning = false,
  onDismissLoading,
  readOnly = false,
  emptyMessage = "No posts found. Try refreshing.",
  loadingMessage = "Finding relevant posts…",
}) => {
  /** URL of the card whose length menu is open, if any. */
  const [lengthMenuFor, setLengthMenuFor] = useState<string | null>(null);
  const lengthMenuRef = useRef<HTMLDivElement>(null);

  // Dismiss the length menu on an outside click, the usual expectation.
  useEffect(() => {
    if (lengthMenuFor === null) return;
    const onDocClick = (e: MouseEvent) => {
      if (lengthMenuRef.current && !lengthMenuRef.current.contains(e.target as Node)) {
        setLengthMenuFor(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [lengthMenuFor]);

  // A new batch remounts cards (keyed by URL). Only the menu, which lives
  // out here, needs closing.
  useEffect(() => {
    setLengthMenuFor(null);
  }, [generatedAt]);

  return (
    <>
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-primary border-t-transparent" />
          <p className="text-sm text-theme-fg-muted">{loadingMessage}</p>
          {onDismissLoading && (
            <button
              type="button"
              onClick={onDismissLoading}
              className="rounded-lg border border-theme-border px-3 py-1.5 text-[11px] font-medium text-theme-fg-muted transition-colors hover:bg-theme-secondary/10"
            >
              Continue in background
            </button>
          )}
        </div>
      )}

      {pacingWarning && !isLoading && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          <span className="font-semibold">Pace yourself.</span>{" "}
          You've replied to several threads recently. Reddit may flag
          accounts that post many branded replies in a short window —
          consider spacing the rest over a few hours.
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {!isLoading && !error && posts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-sm text-theme-fg-muted">{emptyMessage}</p>
        </div>
      )}

      {posts.map((post) => {
        const isRegenerating = regeneratingUrl === post.url;
        const age = formatAge(post.created_utc);
        const isAnswered = answeredUrls.has(post.url);

        return (
          <div
            key={post.url}
            className={`rounded-xl border p-4 space-y-2.5 transition-colors ${
              isAnswered
                ? "border-green-500/40 bg-green-500/5"
                : "border-theme-border bg-theme-background hover:border-theme-fg/20"
            } ${isLoading ? "opacity-50" : ""}`}
          >
            {/* Meta row — subreddit, freshness, how crowded the thread is */}
            <div className="flex flex-wrap items-start gap-x-2 gap-y-1 text-[11px]">
              {/* Subreddit and byline stack; the badge stays on the first line. */}
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="font-semibold text-orange-500">{post.subreddit}</span>
                <span className="flex flex-wrap items-center gap-x-1.5 text-theme-fg-muted">
                  {post.author && <span>submitted by u/{post.author}</span>}
                  {age && <span>{post.author ? "· " : ""}{age}</span>}
                  {post.num_comments !== undefined && post.num_comments !== null && (
                    <span>
                      · {post.num_comments} {post.num_comments === 1 ? "comment" : "comments"}
                    </span>
                  )}
                </span>
              </div>
              {isAnswered ? (
                <span className="ml-auto flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-700">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Replied
                </span>
              ) : (
                post.relevance_score !== undefined &&
                post.relevance_score !== null && (
                  <span
                    className={`ml-auto rounded-full px-2 py-0.5 font-medium ${relevanceStyle(
                      post.relevance_score
                    )}`}
                    title={post.relevance_reason || undefined}
                  >
                    {post.relevance_score}% match
                  </span>
                )
              )}
            </div>

            {/* Thread title — the "Thread:" prefix was noise, the link says it */}
            <p className="text-[13.5px] font-semibold text-theme-fg leading-snug">
              {post.title}
            </p>

            {/* What they actually asked — the thing being answered, so it
                has to be readable in full without leaving the app. */}
            {post.snippet && (
              <ExpandableText
                text={post.snippet}
                lines={3}
                moreLabel="Read full question"
                className="text-[12px] text-theme-fg-muted leading-relaxed whitespace-pre-line"
              />
            )}

            {/* The link is the whole point of the feature, so make it read
                like one: explicit blue (not a theme token that resolves to
                near-body-text), and the redundant scheme/host trimmed. */}
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="truncate">{prettyUrl(post.url)}</span>
            </a>

            {/* The idea for a reply — not a paste-ready draft. Reddit's spam
                detection flags and removes comments that read as pasted AI
                output, so the text is deliberately not selectable and there
                is no copy button: reading it and then typing your own
                version in your own words is the point, not shortcutting it.
                Only shown when there is a draft: guard keeps the layout
                clean when a post has no reply yet (e.g. during a slow first
                batch). */}
            {post.suggested_reply && (
            <div className="rounded-lg border border-theme-border/60 bg-theme-secondary/5 p-3 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-theme-fg-muted">
                Reply idea — write this in your own words on Reddit
              </p>
              <p
                onCopy={(e) => e.preventDefault()}
                className={`select-none whitespace-pre-wrap text-[12px] leading-relaxed ${
                  isRegenerating ? "text-theme-fg-muted opacity-50" : "text-theme-fg"
                }`}
              >
                {post.suggested_reply}
              </p>
              <div className="flex items-center justify-between gap-2 pt-0.5">
                {/* Posting is manual, so only the user knows when a thread
                    is done. Marking it keeps it out of every future feed.
                    Not shown read-only (the Replied tab) — the card is
                    already the record of that action. */}
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => onToggleAnswered(post.url)}
                    title={
                      isAnswered
                        ? "Unmark — it can appear in future feeds again"
                        : "Mark as replied — it won't show up again"
                    }
                    // Green is reserved for the done state — tinting the
                    // unmarked button made it read as already replied.
                    className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      isAnswered
                        ? "border-green-600 bg-green-600 text-white hover:bg-green-700"
                        : "border-theme-border text-theme-fg hover:border-theme-fg/40 hover:bg-theme-secondary/10"
                    }`}
                  >
                    <span
                      className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 ${
                        isAnswered
                          ? "border-white bg-white text-green-600"
                          : "border-theme-fg-muted/60"
                      }`}
                    >
                      {isAnswered && (
                        <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span>{isAnswered ? "Replied" : "Mark replied"}</span>
                  </button>
                ) : (
                  <span />
                )}
                {!readOnly && (
                  /* Length is picked per card, at the moment of regenerating —
                     "rewrite this one, shorter" is a single action. */
                  <div className="relative shrink-0" ref={lengthMenuFor === post.url ? lengthMenuRef : undefined}>
                    <button
                      type="button"
                      onClick={() => setLengthMenuFor(lengthMenuFor === post.url ? null : post.url)}
                      disabled={isRegenerating}
                      title="Get a different idea"
                      className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-theme-fg-muted transition-colors hover:bg-theme-secondary/10 disabled:opacity-50"
                    >
                      <svg
                        className={`h-3 w-3 ${isRegenerating ? "animate-spin" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>{isRegenerating ? "Writing…" : "Regenerate"}</span>
                    </button>

                    {lengthMenuFor === post.url && !isRegenerating && (
                      <div className="absolute bottom-full right-0 z-20 mb-1 w-44 rounded-xl border border-theme-border bg-theme-surface p-1 shadow-xl">
                        {LENGTH_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setLengthMenuFor(null);
                              onRegenerate(post.url, opt.value);
                            }}
                            className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[12px] text-theme-fg transition-colors hover:bg-theme-secondary/10"
                          >
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-theme-fg-muted">{opt.hint}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        );
      })}
    </>
  );
};
